import { resolveRoute, useLocation } from "@hakili/app-shell";
import type { BuyerFeesAnswer, CatalogueCard, VehicleDetailAnswer } from "@hakili/contracts";
import { t } from "@hakili/i18n";
import { DemandeFonctionnaliteProvider } from "@hakili/ui";

import type { VehicleCard } from "./routes/carte.js";
import { Profil } from "./routes/profil.js";
import { ProfilDeclare } from "./routes/profil-declare.js";
import type { ProfileView } from "./routes/declared-profile.js";
import type { ProfileBasis } from "./routes/profile-basis.js";
import { PLAFOND_SANS_LIMITE, Recherche } from "./routes/recherche.js";
import { PaymentScreen } from "./routes/payment-screen.js";
import { type ClientOfferView, Offres } from "./routes/offres.js";
import { Suivi } from "./routes/suivi.js";
import { PickupVoucherScreen, type VoucherView } from "./routes/retrait.js";
import { CashScreen, type CashView } from "./routes/comptant.js";
import { VehicleSheet, type VehicleDetail, type VerificationFeeQuote } from "./routes/vehicule.js";
import { QueueFrontScreen, type FrontQueueEntry } from "./routes/queue-front.js";
import { Eligibility, type DeclaredLatest, type Issue } from "./routes/eligibilite.js";
import { ExistingAccount } from "./routes/existing-account.js";
import { NoWayOut } from "./routes/no-way-out.js";
import { Visit } from "./routes/visite.js";
import { attendLeClient } from "./shell/attente.js";
import { useCallback, useEffect, useState } from "react";

import { Connexion } from "./routes/connexion.js";
import { DepositPage } from "./routes/deposit.js";
import {
  type ControlCoverage,
  Dossier,
  type DossierView,
  type FeesView,
  type FileFeeView,
} from "./routes/dossier.js";
import { ECRANS } from "./routes/ecrans.js";
import { Bienvenue, ChoixCode, CodeVerif, Numero, ProfilInscription } from "./routes/inscription.js";
import type { ClientDocuments } from "./session/documents.js";
import { offerTermsFrom } from "./session/offer-terms.js";
import { type Coverage, type Handsets, useSession } from "./session/useSession.js";
import { ClientShell } from "./shell/ClientShell.js";
import { messageOf } from "./session/session.js";
import { ROUTES, TOUS_LES_ECRANS } from "./shell/routes.js";
import { creerClientStore } from "./shell/store.js";

/**
 * `P1-CLW-007`, `P1-CLW-008` and `P1-CLW-024`: the client journey, assembled.
 *
 * **This file is the sentence that was missing, and it is short on purpose.**
 * `P0-UIK-016` built the routes and the store, `P0-UIK-020` the bundler, and
 * `main.tsx` opened a React root and rendered `null` , the same gap the console
 * had before round 168, found the same way: by a person opening a browser. If
 * assembling finished parts needed a hundred lines of new logic, the parts would
 * be the wrong shape.
 *
 * **The wizard is a STATE and not a route**, and that is WF-CLI-01's shape
 * rather than a shortcut. Its five screens are one flow with one back arrow;
 * giving each a URL would let a person deep-link into `choixcode` with no
 * verified number behind them, and that screen would then have to decide what to
 * do about it. The authenticated app keeps its paths , 4.6.4's `/dossier` and
 * its siblings , because those ARE places a person returns to.
 */

/** The shapes the API answers with, read defensively at the boundary. */
/** Round `PAY-P`, `M3`: the coverage the session attached, read as sent. */
const coverageFrom = (value: unknown): ControlCoverage | null => {
  if (typeof value !== "object" || value === null) return null;
  const one = value as Record<string, unknown>;
  if (typeof one["covered"] !== "boolean") return null;
  const day = one["nextControlDay"] as { label?: unknown; dayOn?: unknown } | null | undefined;
  const nextControlDay =
    typeof day === "object" && day !== null && typeof day.label === "string"
      ? { label: day.label, dayOn: typeof day.dayOn === "string" ? day.dayOn : "" }
      : null;
  return {
    city: typeof one["city"] === "string" ? one["city"] : null,
    covered: one["covered"],
    cabinetName: typeof one["cabinetName"] === "string" ? one["cabinetName"] : null,
    nextControlDay,
  };
};

/**
 * `FX-1b`, `M2`: **exported so the feed can be PROVEN.**
 *
 * It was module-private, so nothing could assert that a field the door answers survives the
 * parse. That is precisely where a field dies quietly: the door is right, the screen is right,
 * every spec is green, and the value never arrives , `R40`'s whole reason.
 */
export const dossierFrom = (row: unknown): DossierView | null => {
  if (typeof row !== "object" || row === null) return null;
  const value = row as Record<string, unknown>;
  const reference = value["reference"];
  /**
   * `round CW`, `CW.5`: **the stage and its LABEL are two fields, and this line
   * read one of them twice.**
   *
   * `value["stage"] ?? value["etape"]` prefers the wire word, so the first
   * `Suivi` pair printed « expertise » as the card's title while the timeline
   * beside it printed « Vérification du véhicule » from the same answer. The
   * door has always sent both , `etape` is `etapeLabel`'s French and `stage` is
   * 2.4.1's id , and the screen needs each for a different thing: the id to
   * mark the step it is on, the label to be read by a person.
   *
   * The fallback survives for `stage` alone, because a build that met an older
   * answer should show the word it has rather than nothing at all.
   */
  const stage = value["stage"] ?? value["etape"];
  const etape = typeof value["etape"] === "string" ? value["etape"] : stage;
  if (typeof reference !== "string" || typeof stage !== "string" || typeof etape !== "string") return null;
  const chemin = value["chemin"];
  return {
    id: typeof value["id"] === "string" ? value["id"] : "",
    // `C3-A.2`: the DOOR's own word for what he owes, carried untouched.
    geste: value["geste"] === "fee" || value["geste"] === "launch" ? value["geste"] : null,
    sellerName: typeof value["sellerName"] === "string" ? value["sellerName"] : "",
    // `P1-DOS-010`: the chargé the birth chose, or `null` for the exception.
    ownerName: typeof value["ownerName"] === "string" ? value["ownerName"] : null,
    // `L-1`, `M1`: the file's own terms; absent or null reads a dash, never a default.
    depositPct: typeof value["depositPct"] === "number" ? value["depositPct"] : null,
    durationMonths: typeof value["durationMonths"] === "number" ? value["durationMonths"] : null,
    // `C7.4`: the civility, `null` until the chargé read it; `C7.1`: the intention.
    clientGender:
      value["clientGender"] === "f" || value["clientGender"] === "m" ? value["clientGender"] : null,
    intention: typeof value["intention"] === "string" ? value["intention"] : "",
    reference,
    vehicleLabel: typeof value["vehicleLabel"] === "string" ? value["vehicleLabel"] : "",
    etape,
    attente: typeof value["attente"] === "string" ? value["attente"] : "",
    // `CW.5`: the chip's TONE travels with its sentence. A screen that chose a
    // colour by comparing French would show a grey chip on a finished file the
    // first time a word was corrected.
    ton: typeof value["ton"] === "string" ? value["ton"] : "",
    stage,
    // I20: the path the API computed, rendered as given. A screen that appended
    // "what comes next" would show a refused file a bank stage it never reaches.
    //
    // `CW.5`: it carries the id BESIDE the label now. A row that is only a
    // string is skipped rather than rendered with an empty key , the screen
    // shows one step fewer, never a step it cannot mark.
    chemin: Array.isArray(chemin)
      ? chemin.flatMap((one) => {
          if (typeof one !== "object" || one === null) return [];
          const step = one as Record<string, unknown>;
          return typeof step["id"] === "string" && typeof step["label"] === "string"
            ? [{ id: step["id"], label: step["label"] }]
            : [];
        })
      : [],
    // `round W-13`, `M1`: the verdict and the figures the card argues with,
    // the door's own; every one read as sent, none composed here.
    verdict:
      value["verdict"] === "conforme" ||
      value["verdict"] === "ecartprix" ||
      value["verdict"] === "nonconforme"
        ? value["verdict"]
        : null,
    report: reportFrom(value["report"]),
    feeClientAmount: typeof value["feeClientAmount"] === "number" ? value["feeClientAmount"] : null,
    refund: refundFrom(value["refund"]),
    closure: closureFrom(value["closure"]),
    breachRefund: breachRefundFrom(value["breachRefund"]),
    sellerPhone: typeof value["sellerPhone"] === "string" ? value["sellerPhone"] : null,
    controlPrevented: preventedFrom(value["controlPrevented"]),
    controlCoverage: coverageFrom(value["controlCoverage"]),
    // `L-1b`, `M5` (c): the offers received, COUNTED by the door and never named (I8).
    offersReceived: typeof value["offersReceived"] === "number" ? value["offersReceived"] : 0,
    // R23 (b): the year, the catalog's, printed beside the label as the drawing does.
    vehicleYear: typeof value["vehicleYear"] === "number" ? value["vehicleYear"] : null,
    // `L-2`, `M4` (A8): the retained offer, read as sent.
    retainedOffer: retainedOfferFrom(value["retainedOffer"]),
    // `L-3`, `M2` (WF-CLI-20): the study in aggregate and the one block, read as sent.
    study: studyFrom(value["study"]),
    completionRequest: completionRequestFrom(value["completionRequest"]),
    // `L-2`, `M5`: the warranty's decision, the door's word or null.
    warrantySettlement:
      value["warrantySettlement"] === "comptant" || value["warrantySettlement"] === "retiree"
        ? value["warrantySettlement"]
        : null,
    // `T-1b`, `M3`: the conditions as facts and the money's two transfers, read as sent.
    conditions: conditionsFrom(value["conditions"]),
    moneyFlow: moneyFlowFrom(value["moneyFlow"]),
    // `K-1`, `M3 (d)`: the days the move has been his on a held file, the door's number or null.
    waitingOnYouDays: typeof value["waitingOnYouDays"] === "number" ? value["waitingOnYouDays"] : null,
  };
};

const conditionsFrom = (raw: unknown): NonNullable<DossierView["conditions"]> | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const bank = value["bank"] as Record<string, unknown> | null | undefined;
  const rows = Array.isArray(bank?.["conditions"]) ? bank["conditions"] : [];
  return {
    bank:
      bank === null || bank === undefined || typeof bank["bankName"] !== "string"
        ? null
        : {
            bankName: bank["bankName"],
            conditions: rows.flatMap((one: unknown) => {
              const row = one as Record<string, unknown>;
              return typeof row["key"] === "string" && typeof row["label"] === "string"
                ? [
                    {
                      key: row["key"],
                      label: row["label"],
                      state: typeof row["state"] === "string" ? row["state"] : "",
                      liftedAt: typeof row["liftedAt"] === "string" ? row["liftedAt"] : null,
                    },
                  ]
                : [];
            }),
            liftedCount: typeof bank["liftedCount"] === "number" ? bank["liftedCount"] : 0,
            total: typeof bank["total"] === "number" ? bank["total"] : 0,
          },
    hakiliFeeSettled: value["hakiliFeeSettled"] === true ? true : null,
    hakiliFeeFinanced: value["hakiliFeeFinanced"] === true,
  };
};

const moneyFlowFrom = (raw: unknown): NonNullable<DossierView["moneyFlow"]> | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const n = (key: string): number | null => {
    const one = value[key];
    return typeof one === "number" ? one : null;
  };
  const salePriceAmount = n("salePriceAmount");
  const ratePct = n("ratePct");
  const dealerAmount = n("dealerAmount");
  const hakiliAmount = n("hakiliAmount");
  return salePriceAmount === null || ratePct === null || dealerAmount === null || hakiliAmount === null
    ? null
    : { salePriceAmount, ratePct, dealerAmount, hakiliAmount };
};

/** I8's literal guard on the buyer's read: no key of it is spelt « bank »; the study is « study ». */
const studyFrom = (raw: unknown): NonNullable<DossierView["study"]> | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const one = raw as Record<string, unknown>;
  const n = (key: string): number => {
    const value = one[key];
    return typeof value === "number" ? value : 0;
  };
  return {
    engaged: n("engaged"),
    studying: n("studying"),
    completionsRequested: n("completionsRequested"),
    offersReceived: n("offersReceived"),
    offersEnded: n("offersEnded"),
    slaDays: typeof one["slaDays"] === "number" ? one["slaDays"] : null,
  };
};

const completionRequestFrom = (raw: unknown): NonNullable<DossierView["completionRequest"]> | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const documents = (raw as { documents?: unknown }).documents;
  if (!Array.isArray(documents)) return null;
  const read = documents.flatMap((one: unknown) => {
    const row = one as Record<string, unknown>;
    return typeof row["code"] === "string" && typeof row["label"] === "string"
      ? [
          {
            code: row["code"],
            label: row["label"],
            helpText: typeof row["helpText"] === "string" ? row["helpText"] : null,
          },
        ]
      : [];
  });
  return read.length === 0 ? null : { documents: read };
};

const retainedOfferFrom = (raw: unknown): NonNullable<DossierView["retainedOffer"]> | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  if (typeof value["offerId"] !== "string" || typeof value["bankName"] !== "string") return null;
  const signature = value["signature"] as Record<string, unknown> | null | undefined;
  const appointment = signature?.["appointment"] as Record<string, unknown> | null | undefined;
  return {
    offerId: value["offerId"],
    bankName: value["bankName"],
    circuit: value["circuit"] === "comite" || value["circuit"] === "signature" ? value["circuit"] : null,
    amountWarranty: typeof value["amountWarranty"] === "number" ? value["amountWarranty"] : 0,
    financesWarranty: value["financesWarranty"] === true,
    // `CLIENT-OFFER-TERMS`: the four terms as served, `null` when the answer carries none (an older answer).
    terms: offerTermsFrom(value["terms"]),
    // `T-1`, `M3`: the figures and the signature, read as sent , the deposit `null` when the door has none.
    depositAmount: typeof value["depositAmount"] === "number" ? value["depositAmount"] : null,
    amountVehicle: typeof value["amountVehicle"] === "number" ? value["amountVehicle"] : 0,
    feeBankAmount: typeof value["feeBankAmount"] === "number" ? value["feeBankAmount"] : 0,
    feeHakiliAmount: typeof value["feeHakiliAmount"] === "number" ? value["feeHakiliAmount"] : 0,
    financesBankFee: value["financesBankFee"] === true,
    financesHakiliFee: value["financesHakiliFee"] === true,
    signature:
      signature === null || signature === undefined
        ? null
        : {
            packSentAt: typeof signature["packSentAt"] === "string" ? signature["packSentAt"] : null,
            contractSignedAt:
              typeof signature["contractSignedAt"] === "string" ? signature["contractSignedAt"] : null,
            appointment:
              appointment !== null &&
              appointment !== undefined &&
              typeof appointment["agence"] === "string" &&
              typeof appointment["when"] === "string"
                ? { agence: appointment["agence"], when: appointment["when"] }
                : null,
            documents: Array.isArray(signature["documents"])
              ? signature["documents"].flatMap((one: unknown) => {
                  const row = one as Record<string, unknown>;
                  return typeof row["code"] === "string" && typeof row["label"] === "string"
                    ? [
                        {
                          code: row["code"],
                          label: row["label"],
                          pageCount: typeof row["pageCount"] === "number" ? row["pageCount"] : 0,
                          state: typeof row["state"] === "string" ? row["state"] : "",
                        },
                      ]
                    : [];
                })
              : [],
            signedCount: typeof signature["signedCount"] === "number" ? signature["signedCount"] : 0,
          },
  };
};

const preventedFrom = (raw: unknown): DossierView["controlPrevented"] => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  if (typeof value["reason"] !== "string" || typeof value["at"] !== "string") return null;
  return {
    reason: value["reason"],
    detail: typeof value["detail"] === "string" ? value["detail"] : null,
    at: value["at"],
  };
};

const reportFrom = (raw: unknown): DossierView["report"] => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  if (typeof value["estimatedValueAmount"] !== "number" || typeof value["askedPriceAmount"] !== "number")
    return null;
  return {
    estimatedValueAmount: value["estimatedValueAmount"],
    askedPriceAmount: value["askedPriceAmount"],
    gapPct: typeof value["gapPct"] === "number" ? value["gapPct"] : null,
    // `PRICE-GAP-DEALER`, M2: the restart threshold and the seller's answer, as the door sent them.
    ...(typeof value["ceilingAmount"] === "number" ? { ceilingAmount: value["ceilingAmount"] } : {}),
    priceAnswer:
      value["priceAnswer"] === "aligne" ||
      value["priceAnswer"] === "propose" ||
      value["priceAnswer"] === "maintenu" ||
      value["priceAnswer"] === "accepte"
        ? value["priceAnswer"]
        : null,
  };
};

/**
 * `K-2` (resumed): the closure's code and reason, as the door answers them; `null` while open.
 *
 * `FX-1b`, `M2`: **and its date.** A parser that read two of three fields would have dropped
 * `at` silently and left Suivi drawing nothing, with every spec still green , which is why
 * `R40` asks for the App's wiring in the same commit as the door.
 */
const closureFrom = (raw: unknown): DossierView["closure"] => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  return {
    code: typeof value["code"] === "string" ? value["code"] : null,
    reason: typeof value["reason"] === "string" ? value["reason"] : null,
    at: typeof value["at"] === "string" ? value["at"] : null,
  };
};

/** `K-2` (resumed): the refund at the dealer's charge , the total and the day confirmed, or `null`. */
const breachRefundFrom = (raw: unknown): DossierView["breachRefund"] => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  if (typeof value["totalAmount"] !== "number") return null;
  return {
    totalAmount: value["totalAmount"],
    confirmedOn: typeof value["confirmedOn"] === "string" ? value["confirmedOn"] : null,
  };
};

const refundFrom = (raw: unknown): DossierView["refund"] => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  return typeof value["reason"] === "string" && typeof value["orderedOn"] === "string"
    ? { reason: value["reason"], orderedOn: value["orderedOn"] }
    : null;
};

/**
 * One row of the search door's answer, from the contract's card to this screen's own names.
 *
 * `GENERIC-DOOR-SWEEP`, Arc 3 (A48): the row is `@hakili/contracts`' {@link CatalogueCard}, the type the door is returned by and the
 * schema an integration spec parses the door's real answer with. This function used to read every key off an `unknown` by
 * hand, and three of those keys were never sent: `distanceKm`, `travelTime` and `priceDropAmount`. The door answers
 * `distance: { kilometres, minutes, basis }` and no drop field on the public card, so the card's distance line and its price-drop
 * strip have been dead in production. They stay `null` here, exactly as they were, because wiring them changes what a buyer
 * sees (the minutes want a sentence, the drop wants a door) and that is a ruling, not a typing change: it is booked in the Arc 3
 * report.
 */
const carteFrom = (row: CatalogueCard): VehicleCard => ({
  id: row.id,
  title: row.title,
  year: row.year,
  stockReference: row.stockReference,
  mileageKm: row.mileageKm,
  gearbox: row.gearbox,
  fuel: row.fuel,
  city: row.city,
  district: row.district,
  distanceKm: null,
  travelTime: null,
  sellerName: row.sellerName,
  reliability: row.reliability,
  priceAmount: row.priceAmount,
  monthlyAmount: row.monthlyAmount,
  photoCount: row.photoCount,
  photoUrl: row.photoUrl,
  priceDropAmount: null,
  // `W-13`, `M2`: the hold, the search door's live word.
  engaged: row.engaged,
});

/**
 * The public fiche, from the contract's {@link VehicleDetailAnswer} to this screen's names. The door sends no distance for a
 * single car (the card's `distance` is the search's), so `distanceKm` and `travelTime` stay `null` as before.
 */
const vehicleFrom = (row: VehicleDetailAnswer): VehicleDetail => ({
  id: row.id,
  title: row.title,
  year: row.year,
  mileageKm: row.mileageKm,
  colour: row.colour,
  gearbox: row.gearbox,
  fuel: row.fuel,
  seats: row.seats,
  city: row.city,
  district: row.district,
  priceAmount: row.priceAmount,
  hasValidReport: row.hasValidReport,
  // `W-13`, `M2`: the hold, the door's live word.
  engaged: row.engaged,
  sellerName: row.seller.name,
  reliability: row.seller.reliability,
  photos: row.photos.map((one) => ({ id: one.id, url: one.url, view: one.view })),
  distanceKm: null,
  travelTime: null,
});

/**
 * The prequalification's issue, read defensively.
 *
 * The union is decided by `eligible`, and a shape this app does not know is
 * `null` , which the screen renders as the form again rather than as a verdict
 * about somebody's money that nobody computed.
 */
const issueFrom = (row: unknown): Issue | null => {
  if (typeof row !== "object" || row === null) return null;
  const answer = row as Record<string, unknown>;
  // The door answers `{ id, issue }`; a caller that already unwrapped it hands
  // the issue itself. `??` cannot yield null here , `answer` is an object , so
  // the two shapes are separated by asking, not by a null check that can never
  // fire.
  const nested = answer["issue"];
  const value = (typeof nested === "object" && nested !== null ? nested : answer) as Record<string, unknown>;
  if (typeof value["eligible"] !== "boolean") return null;
  return value as unknown as Issue;
};

/**
 * `round N+3`, `S2-14`: the latest DECLARATION, read defensively, for
 * `WF-CLI-05`'s pre-fill.
 *
 * **`bankName` is read from nowhere here, by construction.** The five fields
 * below are read by NAME, one by one, off `answer["declared"]` , there is no
 * spread that could carry a sixth key the door never sends, and the door
 * never sends one (`PrequalificationsController.mine`'s own comment).
 */
const declaredFrom = (row: unknown): DeclaredLatest | null => {
  if (typeof row !== "object" || row === null) return null;
  const declared = (row as Record<string, unknown>)["declared"];
  if (typeof declared !== "object" || declared === null) return null;
  const value = declared as Record<string, unknown>;
  const netSalaryAmount = value["netSalaryAmount"];
  const employment = value["employment"];
  const existingLoanInstalmentAmount = value["existingLoanInstalmentAmount"];
  const declaredAt = value["declaredAt"];
  const fresh = value["fresh"];
  if (
    typeof netSalaryAmount !== "number" ||
    typeof employment !== "string" ||
    typeof existingLoanInstalmentAmount !== "number" ||
    typeof declaredAt !== "string" ||
    typeof fresh !== "boolean"
  ) {
    return null;
  }
  return { netSalaryAmount, employment, existingLoanInstalmentAmount, declaredAt, fresh };
};

/**
 * `round C7`, `C7.1`: **« Ce que vous payez à Hakili », composed from three
 * doors and no arithmetic.** The verification fee's figure is `/fees`', its
 * state is the file's `geste` (« fee » while the buyer owes it, settled from
 * the moment he could launch); the frais de dossier's figure is `payments`'
 * quote on the file, its state the mandate's own choice until the retained
 * bank's decision exists (the lending round , flagged in the addendum), and
 * « Réglé » once the file is disbursed.
 */
const feesFrom = (
  dossier: DossierView,
  answer: {
    readonly fees: BuyerFeesAnswer;
    readonly mandate: unknown;
  },
): FeesView => {
  const fees = answer.fees;
  const mandate = answer.mandate as {
    mandate?: { accepted?: { feeFinancingRequested?: unknown } | null };
  } | null;
  const clientAmount = fees.fee.clientAmount;
  const hakiliAmount = fees.fileFees?.hakiliAmount;
  const financed = mandate?.mandate?.accepted?.feeFinancingRequested === true;
  return {
    verification:
      typeof clientAmount === "number" ? { amount: clientAmount, settled: dossier.geste !== "fee" } : null,
    file:
      typeof hakiliAmount === "number"
        ? {
            amount: hakiliAmount,
            status: dossier.stage === "decaisse" ? "regle" : financed ? "auDeblocage" : "aLaSignature",
          }
        : null,
  };
};

export function App({ origin }: { readonly origin: string }): React.JSX.Element {
  const app = useSession(origin);
  // Round 203: the browser's own path, and `naviguer` so a tab tap does not
  // reload. A reload would drop the in-memory access token and cost a refresh
  // round trip on a Bamako handset , 166 §1.2 is why the token is in memory,
  // and this is what keeps that cheap.
  const here = useLocation();
  const {
    session,
    queryClient,
    aller,
    deconnecter,
    submitFeatureRequest,
    searchVehicles,
    loadBuyerLists,
    keepVehicle,
    passVehicle,
    clearPassed,
    loadFrontQueue,
    loadFlags,
    loadMakes,
    loadCities,
    loadVehicle,
    quoteEstimate,
    loadCoverage,
    loadVerificationFeeQuote,
    askVisit,
    prequalifyFromProfile,
    mePrequalification,
    openDossier,
  } = app;
  const [store] = useState(() => creerClientStore());

  const [dossier, setDossier] = useState<DossierView | null>(null);
  /** `C7.1`: the buyer's own pieces door's answer, `null` until it answered. */
  const [documents, setDocuments] = useState<ClientDocuments | null>(null);
  const [fees, setFees] = useState<FeesView | null>(null);
  // `T-1c`, `M2`: the buyer's file fee by his own door (WF-CLI-24 step 5), `null` while none is born.
  const [fileFee, setFileFee] = useState<FileFeeView | null>(null);
  // `T-2`: the buyer's bon de retrait, read on Suivi and on /dossier/retrait from `decaisse` on.
  const [voucher, setVoucher] = useState<VoucherView | null | undefined>(undefined);
  // `T-2b`: the cash settlement on a comptant file, read on Suivi and on /dossier/comptant.
  const [cash, setCash] = useState<CashView | null | undefined>(undefined);

  /**
   * `round CW`, `CW.2`: everything « Trouvez votre voiture » shows, and every
   * one of them from a door.
   *
   * The filters live HERE and not inside the screen, because they are what the
   * door is asked , changing one is a new question, not a re-render , and the
   * screen stays a rendering of an answer.
   */
  const [park, setPark] = useState<readonly VehicleCard[]>([]);
  const [everywhere, setEverywhere] = useState<readonly VehicleCard[]>([]);
  const [favourites, setFavourites] = useState<readonly string[]>([]);
  const [passedList, setPassedList] = useState<readonly string[]>([]);
  /** `AVAILABILITY-QUEUE`, `M2`, point 3: the client's own front positions. */
  const [frontQueue, setFrontQueue] = useState<readonly FrontQueueEntry[]>([]);
  const [makes, setMakes] = useState<readonly string[]>([]);
  const [cities, setCities] = useState<readonly string[]>([]);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [city, setCity] = useState("Bamako");
  const [radius, setRadius] = useState(9999);
  /**
   * `round C3`, `C3.0(c)`: **the default is « Sans limite », the founder's word
   * of 10 September.**
   *
   * It was 150 000 FCFA a month, which `03` `WF-CLI-03` §4 names as the
   * slider's default. That was written when the ceiling only DIMMED a card;
   * since the founder's decision of 9 September a ceiling FILTERS, so the first
   * screen a buyer ever saw had already hidden most of the catalogue before he
   * touched anything , on the pair park, one car of five.
   *
   * **A ceiling is a choice made inside « Filtres », never a surprise.** The
   * top notch sends no ceiling at all (`PLAFOND_SANS_LIMITE`), so the first
   * screen is the whole parc. `03` `WF-CLI-03` §4's default is amended by
   * record, both texts kept, flagged for his walk.
   */
  const [budget, setBudget] = useState(PLAFOND_SANS_LIMITE);
  const [make, setMake] = useState("");
  // `C2.5`: the chosen order lives here, because it is a QUERY and not a view.
  const [tri, setTri] = useState("mensualite");
  const [text, setText] = useState("");

  /** `CW.3`: the one car a buyer is looking at, and the simulator's constants. */
  const [vehicleDetail, setVehicle] = useState<VehicleDetail | null>(null);
  /** `CW.3`: the buyer standing on « Voir le véhicule », and his intent card. */
  /**
   * `CW.4`: the simulation the fiche stored, and the door's answer.
   *
   * `WF-CLI-04`'s acceptance criterion is that Éligibilité cannot be reached
   * without a simulation, so the screen refuses to render one that has none
   * rather than inventing an instalment to ask about.
   */
  const [simulation, setSimulation] = useState<{
    readonly vehicleId: string;
    readonly monthlyAmount: number;
    /** `C3.0(f)`: the two figures the favourable card opens with , the door's,
     *  carried from the fiche exactly as the instalment already was. */
    readonly amountFinanced: number;
    readonly durationMonths: number;
    /** `C3-B.0`: the deposit too, so the mandate reads the buyer's own terms. */
    readonly depositPct: number;
    /** `W-13`, `M2`: the hold as the fiche read it, so « Ouvrir mon dossier » is greyed with the reason. */
    readonly engaged: boolean;
  } | null>(null);
  const [issue, setIssue] = useState<Issue | null>(null);
  /** `C3.0(f)`: what the buyer declared on the form, kept for the bar. */
  const [declared, setDeclared] = useState<{
    readonly netSalaryAmount: number;
    readonly existingLoanInstalmentAmount: number;
  }>({ netSalaryAmount: 0, existingLoanInstalmentAmount: 0 });
  const [issueRefusal, setIssueRefusal] = useState<string | null>(null);
  const [issueBusy, setIssueBusy] = useState(false);
  /**
   * `round N+3`, `S2-14`: the client's own latest declaration, read once per
   * session so `WF-CLI-05`'s form opens pre-filled and so Profil can show it
   * without asking the door twice.
   */
  const [latestDeclaration, setLatestDeclaration] = useState<DeclaredLatest | null>(null);
  /**
   * `REG-PROFILE-001`, Arc B: **what the eligibility check will read**, as the API derives it from the declared
   * profile. Read each time the screen is entered, so it is never older than the server's copy.
   */
  const [profileBasis, setProfileBasis] = useState<ProfileBasis | null>(null);
  const [visiting, setVisiting] = useState(false);
  const [intentRefusal, setIntentRefusal] = useState<string | null>(null);
  const [intentOpen, setIntentOpen] = useState(false);
  /** `C3-B.2`: the fiche's coverage, read beside the car. */
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  /** The amendment: the verification fee for the car, by payments, read beside it. */
  const [verificationFee, setVerificationFee] = useState<VerificationFeeQuote | null>(null);

  /**
   * The buyer's own file, read once he is in.
   *
   * A failure here is deliberately NOT a refusal of the session: a person who is
   * signed in and whose file could not be fetched should see the app with an
   * empty file, not be thrown back to a login they already passed.
   */
  /**
   * `round C3-A`, `C3-A.0(c)`: **re-read the file, because a birth is not a
   * render.**
   *
   * The effect below fires on the session's STATUS , « he just got in » , and
   * on nothing else, which is right and was the whole of round 185's cure. But
   * a file can now be born mid-session, by the buyer's own « Ouvrir mon
   * dossier », and the screen he lands on reads this state: without a re-read
   * he arrived at « Suivi » and it said « Vous n'avez pas encore de dossier. »
   * , the third face of the same finding, and the same sentence he met at the
   * start. The file existed; the screen had never asked again.
   *
   * Extracted rather than repeated so the two callers cannot drift into
   * disagreeing about what « the file » means.
   */
  const relireLeDossier = useCallback(async (): Promise<DossierView | null> => {
    const answer = await app.chargerDossier();
    const sien = dossierFrom(answer.dossier);
    setDossier(sien);
    setDocuments(answer.documents);
    return sien;
  }, [app]);

  useEffect(() => {
    if (session.status !== "authenticated") return undefined;
    const run = { live: true };
    void (async () => {
      try {
        const answer = await app.chargerDossier();
        if (!run.live) return;
        const sien = dossierFrom(answer.dossier);
        setDossier(sien);
        setDocuments(answer.documents);
        /**
         * **A buyer who has a file is SEATED AT IT**, and round 203's own e2e
         * is what bought this line.
         *
         * Before the shell routed, `App` handed it the literal `"/dossier"`, so
         * a signed-in buyer saw his file wherever he was. Routing on the real
         * path is correct and made `/` resolve to `accueil` , which is honestly
         * unbuilt , so the first walk of the round put a returning buyer in
         * front of a marked stub instead of his own dossier. Six assertions
         * across `journey.spec.ts` and `aller-retour.spec.ts` say otherwise in
         * the product's own words: *"lands on the dossier when a previous /me is
         * already in the profile"*.
         *
         * **Only from the landing path, and only when a file exists.** A
         * redirect that fired from anywhere would take the tab bar's own
         * `Rechercher` away from him the moment he tapped it; one that fired
         * with no file would seat him at nothing. `P1-DOS-034`'s one-live-file
         * rule is what makes "his file" a single well-defined destination.
         */
        if (sien !== null && globalThis.location.pathname === "/") here.naviguer("/dossier");
      } catch {
        if (run.live) setDossier(null);
      }
    })();
    return () => {
      run.live = false;
    };
    // **The dependency is the STATUS and the stable CALLBACK, never the object
    // that holds it.** This comment said exactly that before round 185 and the
    // array beside it listed `app` anyway , *"a new object arrives on every
    // render and a dependency on it would refetch the file forever"* , and on
    // 29 August 2026 it did: each fetch's success wrote state, re-rendered,
    // changed `app`'s identity and re-fired the fetch, at about seven and a half
    // requests per second on an idle screen in front of a founder.
    //
    // `chargerDossier` is a `useCallback` over `[api]`, so it is stable for the
    // life of the client; `session.status` is the transition this effect is
    // actually about , "he just got in". **A comment cannot hold a dependency
    // array. `repos.spec.ts` counts.**
    // `here.naviguer` is a `useCallback` over `[]`, so it is as stable as
    // `chargerDossier` and adding it cannot reintroduce round 185's refetch
    // loop. `repos.spec.ts` counts the array, so it is listed rather than
    // explained away.
  }, [session.status, app.chargerDossier, here.naviguer]);

  /**
   * The buyer's own two lists and the flags, read ONCE per session.
   *
   * They do not depend on the filters, so re-reading them on every slider move
   * would be a request per keystroke , the shape that put seven and a half
   * requests a second on an idle screen in round 185. The dependency is the
   * STATUS and the stable callbacks, never the object that holds them.
   */
  useEffect(() => {
    if (session.status !== "authenticated") return undefined;
    const run = { live: true };
    void (async () => {
      try {
        const [lists, flags, queue] = await Promise.all([
          loadBuyerLists(),
          loadFlags(),
          loadFrontQueue().catch(() => [] as readonly FrontQueueEntry[]),
        ]);
        if (!run.live) return;
        setFavourites(lists.favourites);
        setPassedList(lists.passed);
        setDiscoverOpen(flags["client_discover_enabled"] === true);
        setFrontQueue(queue);
      } catch {
        // A buyer with no lists yet is not an error, and neither is a flag door
        // that refused: the screen shows the list without them rather than
        // showing nothing.
        if (run.live) setDiscoverOpen(false);
      }
    })();
    return () => {
      run.live = false;
    };
  }, [session.status, loadBuyerLists, loadFlags, loadFrontQueue]);

  /**
   * `round N+3`, `S2-14`: the client's own latest declaration, read once per
   * session , `WF-CLI-05`'s form and Profil's new row both read the state
   * this writes rather than asking the door again.
   */
  useEffect(() => {
    if (session.status !== "authenticated") return undefined;
    const run = { live: true };
    void (async () => {
      try {
        const answer = await mePrequalification();
        if (run.live) setLatestDeclaration(declaredFrom(answer));
      } catch {
        // No declaration yet is this door's normal `null`, not a refusal ,
        // and a door that genuinely refused leaves the form exactly as blank
        // as it always opened before this round.
        if (run.live) setLatestDeclaration(null);
      }
    })();
    return () => {
      run.live = false;
    };
  }, [session.status, mePrequalification]);

  const loadPrequalificationBasis = app.loadPrequalificationBasis;
  useEffect(() => {
    if (session.status !== "authenticated" || here.path !== "/eligibilite") return undefined;
    const run = { live: true };
    setProfileBasis(null);
    void loadPrequalificationBasis()
      .then((answer) => {
        if (run.live) setProfileBasis(answer);
      })
      .catch((failure: unknown) => {
        if (run.live) setIssueRefusal(messageOf(failure, t("common.error.reseau")));
      });
    return () => {
      run.live = false;
    };
  }, [session.status, here.path, loadPrequalificationBasis]);

  /**
   * The vehicleDetail, read when the path names a car.
   *
   * The simulator's constants come with it and never from the bundle: a screen
   * carrying its own rate would be the divergence between the teaser and the
   * real offer that `01` calls the single worst product failure.
   */
  useEffect(() => {
    const found = /^\/vehicule\/(?<id>[^/]+)$/u.exec(here.path);
    const id = found?.groups?.["id"];
    if (session.status !== "authenticated" || id === undefined) {
      setVehicle(null);
      return undefined;
    }
    const run = { live: true };
    void (async () => {
      const [car, cover, feeQuote] = await Promise.all([
        loadVehicle(id).catch(() => null),
        loadCoverage(id).catch(() => null),
        loadVerificationFeeQuote(id).catch(() => null),
      ]);
      if (!run.live) return;
      setVehicle(car === null ? null : vehicleFrom(car));
      setCoverage(cover);
      setVerificationFee(feeQuote);
    })();
    return () => {
      run.live = false;
    };
  }, [session.status, here.path, loadVehicle, loadCoverage, loadVerificationFeeQuote]);

  /** The reference lists the folded filters offer, read once. */
  useEffect(() => {
    if (session.status !== "authenticated") return undefined;
    const run = { live: true };
    void (async () => {
      // A refused reference door is not a reason to leave the screen without a
      // list: the fold simply offers fewer choices. An uncaught rejection here
      // would also be an unhandled failure in the console, which `repos.spec`
      // asserts against , and rightly.
      const [makes, cities] = await Promise.all([loadMakes().catch(() => []), loadCities().catch(() => [])]);
      if (!run.live) return;
      setMakes(makes);
      setCities(cities);
    })();
    return () => {
      run.live = false;
    };
  }, [session.status, loadMakes, loadCities]);

  /**
   * The search itself, re-asked whenever the buyer changes the QUESTION.
   *
   * The door is asked TWICE: once with the radius and once without it. The
   * second answer is what « plus loin dans le pays » is counted from and, since
   * ruling 4, what each segment's count is read off , `WF-CLI-03` step 8's
   * rule, and the drawing widens it: never the segment, never the free text.
   */
  useEffect(() => {
    if (session.status !== "authenticated") return undefined;
    const run = { live: true };
    void (async () => {
      /**
       * `round C2`, `C2.6`: **the door's OWN parameter names.**
       *
       * This sent `city` and `make`; `GET /api/v1/vehicles` reads `ville` and
       * `marque`. Two of the five filters therefore reached nobody: the buyer
       * chose « Toyota » and the door answered every make, which is exactly
       * what the founder met , three promised further away, none shown, because
       * the count was a difference between two queries that differed only by
       * the radius.
       *
       * Nothing here translates: the door named these parameters and the screen
       * says what the door named.
       */
      const query: Record<string, string> = {
        rayonKm: String(radius),
        /**
         * `round C3`, `C3.0(e)`: **`depuisVille` and never `ville`.**
         *
         * `ville` is the door's filter on where the CAR is. This screen sent
         * the buyer's own position there, so « Tout le Mali » meant « every car
         * in the town I am standing in », and the founder standing in Ségou was
         * shown nothing at all , the parc being a Bamako parc.
         *
         * Where he stands decides the origin the distance is measured FROM and
         * the radius is measured against, and nothing else.
         */
        depuisVille: city,
        // `C2.5`: the chosen order travels, and the door answers a sequence.
        tri,
      };
      /**
       * `round C2`, ruling 5: **at the slider's top notch NO CEILING is sent.**
       *
       * The range stops at `PLAFOND_SANS_LIMITE` and the ceiling filters since
       * the founder's decision, so sending it at the top position would hide
       * every car above 300 000 FCFA a month from everybody, at every setting.
       * The screen labels that position « Sans limite »; this is the half that
       * makes the label true, and the number lives in ONE place so the two
       * cannot disagree.
       */
      if (budget < PLAFOND_SANS_LIMITE) query["maxMensualite"] = String(budget);
      if (make !== "") query["marque"] = make;
      if (text.trim() !== "") query["q"] = text.trim();
      /**
       * `C2.6`: the second query is the FIRST one without its radius.
       *
       * It used to carry the ceiling and the city alone, so « plus loin dans le
       * pays » counted cars the make and the free text would have excluded , a
       * promise the tap could not keep. Built by removing one key, so the two
       * cannot drift apart again.
       */
      const partoutQuery = { ...query };
      delete partoutQuery["rayonKm"];
      const [dansLeRayon, partout] = await Promise.all([
        searchVehicles(query).catch(() => []),
        searchVehicles(partoutQuery).catch(() => []),
      ]);
      if (!run.live) return;
      const rows = dansLeRayon.map(carteFrom);
      setPark(rows);
      /**
       * `round C2`, ruling 4: the rows themselves, not a number derived from
       * them. The segment counts are read off these , what each segment would
       * SHOW under the ceiling and the make , and « plus loin dans le pays » is
       * the difference between the two lists, derived where it is rendered.
       */
      setEverywhere(partout.map(carteFrom));
    })();
    return () => {
      run.live = false;
    };
  }, [session.status, searchVehicles, budget, radius, city, make, text, tri]);

  /**
   * `C7.1`: the fees card's three doors, asked when the buyer is on his file
   * and the file is known. `loadPaiement` is a `useCallback` over `[api]`.
   */
  const dossierId = dossier?.id ?? null;
  const dossierGeste = dossier?.geste ?? null;
  const dossierStage = dossier?.stage ?? null;

  /**
   * Round `L-2`, `M2`: the buyer's offers, read by his own door when he is on
   * the offers screen , every figure the bank's own, nothing composed here.
   * `null` while the door has not answered; re-read after an acceptance.
   */
  /**
   * Round `PB-2`, `M4`: the handsets and this handset's notices, read when « Mon compte » is open and
   * re-read after each gesture on them. `null` while the door has not answered.
   */
  const [handsets, setHandsets] = useState<Handsets | null>(null);
  const loadDevices = app.loadDevices;
  const reloadHandsets = useCallback(async (): Promise<void> => {
    setHandsets(await loadDevices().catch(() => null));
  }, [loadDevices]);
  useEffect(() => {
    if (session.status !== "authenticated" || here.path !== "/profil") return undefined;
    const run = { live: true };
    void loadDevices()
      .catch(() => null)
      .then((answer) => {
        if (run.live) setHandsets(answer);
      });
    return () => {
      run.live = false;
    };
  }, [session.status, here.path, loadDevices]);
  /**
   * `REG-PROFILE-001`, Arc B: the declared profile the editor opens on, read each time the screen is entered so it
   * never shows a copy older than the server's. `null` while the door has not answered.
   */
  const [profileView, setProfileView] = useState<ProfileView | null>(null);
  const [profileRefusal, setProfileRefusal] = useState<string | undefined>(undefined);
  /** `CLIENT-PROFILE-RETURN`: the sentences the last profile save served, handed to « Mon compte » once. */
  const [profileNotices, setProfileNotices] = useState<readonly string[]>([]);
  const loadProfile = app.loadProfile;
  useEffect(() => {
    if (session.status !== "authenticated" || here.path !== "/profil/declaration") return undefined;
    const run = { live: true };
    setProfileRefusal(undefined);
    void loadProfile()
      .then((answer) => {
        if (run.live) setProfileView(answer);
      })
      .catch((failure: unknown) => {
        if (run.live) setProfileRefusal(messageOf(failure, t("common.error.reseau")));
      });
    return () => {
      run.live = false;
    };
  }, [session.status, here.path, loadProfile]);
  const [offers, setOffers] = useState<readonly ClientOfferView[] | null>(null);
  const [offersWord, setOffersWord] = useState<string | null>(null);
  const loadOffers = app.loadOffers;
  useEffect(() => {
    if (session.status !== "authenticated" || here.path !== "/dossier/offres") return undefined;
    const run = { live: true };
    setOffersWord(null);
    void loadOffers()
      .then((rows) => {
        if (run.live) setOffers(rows);
      })
      .catch((failure: unknown) => {
        if (run.live) {
          setOffers([]);
          setOffersWord(
            (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
          );
        }
      });
    return () => {
      run.live = false;
    };
  }, [session.status, here.path, loadOffers]);
  useEffect(() => {
    if (session.status !== "authenticated" || dossierId === null || here.path !== "/dossier")
      return undefined;
    const run = { live: true };
    void app
      .loadPaiement(dossierId, {
        depositPct: dossier?.depositPct ?? null,
        durationMonths: dossier?.durationMonths ?? null,
      })
      .then((answer) => {
        if (run.live && dossier !== null) setFees(feesFrom(dossier, answer));
      })
      .catch(() => {
        if (run.live) setFees(null);
      });
    return () => {
      run.live = false;
    };
    // `dossier` is read inside and listed by its three facts the card depends
    // on, so a re-read of the SAME file does not ask the doors again.
  }, [session.status, dossierId, dossierGeste, dossierStage, here.path, app.loadPaiement]);
  // `T-1c`: the fee's block on the signature card and the conditions card , asked of the
  // buyer's own door once the file stands at the signature or the conditions.
  useEffect(() => {
    if (
      session.status !== "authenticated" ||
      dossierId === null ||
      here.path !== "/suivi" ||
      // The signature card lives on the file's `offre` stage (the bank's own signature step is not
      // a dossier stage); the fee stands from the retained offer on.
      (dossierStage !== "offre" && dossierStage !== "conditions" && dossierStage !== "decaisse")
    ) {
      setFileFee(null);
      return undefined;
    }
    const run = { live: true };
    void app
      .loadFileFee(dossierId)
      .then((answer) => {
        if (run.live) setFileFee(answer);
      })
      .catch(() => {
        if (run.live) setFileFee(null);
      });
    return () => {
      run.live = false;
    };
  }, [session.status, dossierId, dossierStage, here.path, app.loadFileFee]);

  useEffect(() => {
    if (
      session.status !== "authenticated" ||
      dossierId === null ||
      (here.path !== "/suivi" && here.path !== "/dossier/retrait") ||
      // `T-2b`: a cash file carries its settlement from the option on, whatever the console's stage.
      (dossierStage !== "conditions" && dossierStage !== "decaisse" && dossier?.intention !== "comptant")
    ) {
      setVoucher(undefined);
      return undefined;
    }
    const run = { live: true };
    void app
      .loadVoucher(dossierId)
      .then((answer) => {
        if (run.live) setVoucher(answer);
      })
      .catch(() => {
        if (run.live) setVoucher(null);
      });
    return () => {
      run.live = false;
    };
  }, [session.status, dossierId, dossierStage, here.path, app.loadVoucher]);
  useEffect(() => {
    if (
      session.status !== "authenticated" ||
      dossierId === null ||
      dossier?.intention !== "comptant" ||
      (here.path !== "/suivi" && here.path !== "/dossier/comptant")
    ) {
      setCash(undefined);
      return undefined;
    }
    const run = { live: true };
    void app
      .loadCash(dossierId)
      .then((answer) => {
        if (run.live) setCash(answer);
      })
      .catch(() => {
        if (run.live) setCash(null);
      });
    return () => {
      run.live = false;
    };
  }, [session.status, dossierId, dossier?.intention, here.path, app.loadCash]);
  const acheterComptant = useCallback(
    async (id: string): Promise<void> => {
      setCash(await app.openCash(id));
    },
    [app],
  );
  const declareCash = useCallback(
    async (id: string): Promise<void> => {
      setCash(await app.declareCash(id));
    },
    [app],
  );
  const declareFileFee = useCallback(
    async (id: string, means: string, reference: string): Promise<void> => {
      setFileFee(await app.declareFileFee(id, means, reference));
    },
    [app],
  );

  /**
   * `C7.1`: « Envoyer » on one pièce, then the list re-read from the door ,
   * the screen never marks a row received by itself. `C7.1` step 7: the send,
   * then the file re-read, and the buyer taken to « Suivi » as the drawing does.
   */
  const depositDocument = useCallback(
    async (code: string, file: File, declaredMonths?: number): Promise<void> => {
      if (dossierId === null) return;
      await app.depositDocument(dossierId, code, file, declaredMonths);
      setDocuments(await app.loadDocuments(dossierId));
    },
    [app, dossierId],
  );
  const submitDocuments = useCallback(async (): Promise<void> => {
    if (dossierId === null) return;
    await app.submitDocuments(dossierId);
    await relireLeDossier();
    here.naviguer("/suivi");
  }, [app, dossierId, relireLeDossier, here]);

  const refusal = session.status === "anonymous" ? session.refusal : undefined;

  /**
   * `round C7`, `C7.3`: **the public deposit route renders with no session
   * at all** (6.3.6, `shell/routes.ts`: *"the token IS the authorisation"*).
   * Resolved BEFORE the session guard, because a holder who opened the link
   * from WhatsApp has no account and must never meet the welcome screen; the
   * shell already draws neither the top bar nor the tabs for a public route.
   */
  const resolution = resolveRoute(ROUTES, here.path, TOUS_LES_ECRANS);
  if (resolution.kind === "render" && resolution.route.public === true) {
    const token = resolution.params["token"] ?? "";
    return (
      <ClientShell
        queryClient={queryClient}
        store={store}
        status="authenticated"
        path={here.path}
        ville={null}
        naviguer={here.naviguer}
        pending={<p className="p-6 text-sm text-gray-500">{t("client.session.chargement")}</p>}
        signIn={null}
        buyerOwesGesture={false}
      >
        {() => (
          <DepositPage
            read={() => app.readDepositLink(token)}
            deposit={(file, declaredMonths) => app.depositByLink(token, file, declaredMonths)}
          />
        )}
      </ClientShell>
    );
  }

  if (session.status === "pending") {
    return <p className="p-6 text-sm text-gray-500">{t("client.session.chargement")}</p>;
  }

  if (session.status === "anonymous") {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col gap-4 p-4">
        {session.etape === "bienvenue" ? (
          <Bienvenue
            creer={() => {
              aller("numero");
            }}
            connexion={() => {
              aller("connexion");
            }}
          />
        ) : null}
        {session.etape === "numero" ? <Numero demander={app.demanderCode} refusal={refusal} /> : null}
        {session.etape === "code" ? (
          <CodeVerif
            phone={session.phone}
            canal={session.canal ?? "whatsapp"}
            confirmer={app.confirmerCode}
            renvoyer={async () => {
              await app.demanderCode(session.phone);
            }}
            refusal={refusal}
          />
        ) : null}
        {session.etape === "profil" ? (
          <ProfilInscription enregistrer={app.enregistrer} refusal={refusal} />
        ) : null}
        {session.etape === "compteExistant" ? (
          <ExistingAccount phone={session.phone} confirm={app.confirmReset} cancel={app.abandonReset} />
        ) : null}
        {session.etape === "choixcode" ? <ChoixCode choisir={app.choisirCode} refusal={refusal} /> : null}
        {session.etape === "connexion" ? (
          <Connexion
            phone={session.phone}
            connecter={app.connecter}
            oublie={() => {
              aller("numero");
            }}
            // WF-CLI-02 step 1: `Ce n'est pas vous ?` is the one gesture that
            // makes this handset forget whose it is, and it routes to the number
            // for a fresh OTP , the workflow's own path.
            pasVous={app.changerDeCompte}
            refusal={refusal}
          />
        ) : null}
      </main>
    );
  }

  return (
    // One modal for every stub in the app , `P1-UIK-025`. The provider sits
    // ABOVE the shell so a public `/depot/:token`, which renders with no tab
    // bar and no session, still has somewhere to send a demand.
    <DemandeFonctionnaliteProvider envoyer={submitFeatureRequest}>
      <ClientShell
        queryClient={queryClient}
        store={store}
        status="authenticated"
        // Round 203: the real path. This was the literal `"/dossier"`, which is
        // why eleven declared screens rendered as one.
        path={here.path}
        // `CW.7`: the buyer's OWN city, from `/me`. Never a default , a bar
        // that said « Bamako » to a buyer in Ségou would be the product telling
        // him where he lives.
        ville={session.identity.city === "" ? null : session.identity.city}
        naviguer={here.naviguer}
        pending={<p className="p-6 text-sm text-gray-500">{t("client.session.chargement")}</p>}
        signIn={null}
        /**
         * The drawing's rose dot, derived: no dossier, no dot; a stage the
         * buyer does not owe, no dot. Never a constant (`R81`).
         *
         * **`round C3`, `C3.0(a)`: `stage` and not `etape`, and « a label is
         * not a key » for the third time.** The door answers the stage's
         * LABEL in a field called `etape` , « Collecte des pièces » , and its
         * id in `stage`. This asked `attendLeClient` for the label, which is
         * not a stage id, so `etapeDescriptor` threw, the catch answered
         * `false`, and **the dot could never light for anybody**. It failed in
         * the one direction nothing reports: the guard against an unknown
         * stage did exactly its job, on a value that was never a stage.
         */
        buyerOwesGesture={dossier !== null && attendLeClient(dossier.stage)}
      >
        {(ecran, params) => {
          // `round CW`, `CW.1`: Profil is a REAL screen now and needs the
          // buyer's own identity, so it is rendered here where `/me`'s answer
          // lives rather than through the stub table.
          // `session` is already narrowed to `authenticated` by the guard
          // above this return, so asking again is a comparison that cannot
          // be false.
          // `round CW`, `CW.2`: « Trouvez votre voiture », a real screen now.
          // It is rendered here, where the doors' answers live, rather than
          // through the stub table.
          // `round CW`, `CW.3`: the vehicleDetail. It renders only when the car has
          // been read , a screen built on a `null` would be a page of dashes
          // pretending to be a car.
          // « Voir le véhicule », over the same car. It is a STATE of the fiche
          // and not a route of its own, exactly as the drawing has it: the
          // buyer has not left the vehicle he is looking at.
          if (ecran === "eligibilite") {
            // No simulation, no screen: the buyer is sent back to choose a car
            // rather than shown a form about an instalment nobody named.
            // **Except Profil's own door** (`round N+3`, `S2-14`): that one
            // opens this same form with no vehicle in mind on purpose, to
            // re-declare a salary that is not about any one car, and
            // `standaloneDeclaration` is the flag that says so.
            if (simulation === null) {
              // `S5-4`: a reload keeps the path and loses the simulation (in-memory state): the sentence now carries the way out.
              return (
                <NoWayOut
                  testid="eligibilite-sans-simulation"
                  message={t("client.eligibilite.sansSimulation")}
                  action={t("client.eligibilite.choisirVehicule")}
                  onAction={() => {
                    here.naviguer("/recherche");
                  }}
                />
              );
            }
            return (
              <Eligibility
                basis={profileBasis}
                issue={issue}
                engaged={simulation.engaged}
                /**
                 * `round C3`, `C3.0(f)`: the fiche's own quote, carried.
                 *
                 * The favourable card opens with the financing and its
                 * duration, and prints the instalment above the bar. All three
                 * are `POST /api/v1/simulation/quote`'s answer , the same
                 * `monthlyAmount` this screen then sends to the
                 * prequalification , so the teaser and the door cannot
                 * disagree, which is `01`'s worst failure.
                 *
                 */
                financing={{
                  amountFinanced: simulation.amountFinanced,
                  durationMonths: simulation.durationMonths,
                  monthlyAmount: simulation.monthlyAmount,
                  depositPct: simulation.depositPct,
                }}
                /** What the profile declares, echoed back: the bar's segments are
                 *  shares of the salary, and the favourable issue does not
                 *  carry it because it has no reason to. */
                declared={declared}
                onEditProfile={() => {
                  here.naviguer("/profil/declaration");
                }}
                refusal={issueRefusal}
                busy={issueBusy}
                onSubmit={() => {
                  setIssueBusy(true);
                  setIssueRefusal(null);
                  const chosen = simulation;
                  setDeclared({
                    netSalaryAmount: profileBasis?.netSalaryAmount ?? 0,
                    existingLoanInstalmentAmount: profileBasis?.existingLoanInstalmentAmount ?? 0,
                  });
                  void (async () => {
                    try {
                      // `REG-PROFILE-001`: NO salary, loan or situation is sent. The API reads the declared
                      // profile and refuses an incomplete or stale one with its own sentence.
                      const answer = await prequalifyFromProfile({
                        instalmentAmount: chosen.monthlyAmount,
                      });
                      setIssue(issueFrom(answer));
                    } catch (failure) {
                      setIssueRefusal(messageOf(failure, t("common.error.connexionEchouee")));
                      // A refusal for a profile that changed under him: re-read, so the guard shows.
                      void loadPrequalificationBasis()
                        .then(setProfileBasis)
                        .catch(() => undefined);
                    } finally {
                      setIssueBusy(false);
                    }
                  })();
                }}
                onOpenDossier={(declaration) => {
                  /**
                   * `round C3-A`, `C3-A.0(c)`: **the founder's finding of 11
                   * September, and it was the first of the two.**
                   *
                   * This read `here.naviguer("/dossier")` and nothing else. A
                   * control that never called its door: the buyer pressed
                   * « Ouvrir mon dossier », the app moved to the Dossier tab,
                   * and the tab correctly reported « Vous n'avez pas encore de
                   * dossier. » , because he had none. Nothing errored, nothing
                   * logged, and the screen was telling the truth about a state
                   * the control was supposed to have changed.
                   *
                   * `WF-CLI-05` §6 says the control runs `WF-CLI-08`: the file
                   * is born by `POST /api/v1/me/dossiers` (`P1-DOS-026`, which
                   * has existed since round 192 and which this app had never
                   * called), intent `financement`, and the buyer lands on
                   * « Suivi » §4 with his tracking card , **not** on the
                   * Dossier tab. The drawing agrees in one line of its own:
                   * `setEtat("paiement"); go("suivi")`.
                   *
                   * The refusal path is the other half of the finding. A door
                   * that refuses answers in its own French (`4.5.5`), and the
                   * screen shows it: a gate may close, never in silence.
                   */
                  void (async () => {
                    setIssueBusy(true);
                    setIssueRefusal(null);
                    try {
                      // `L-1`, `M1` (C3-B's ruling (ii)): the buyer's own
                      // deposit and term, as the fiche holds them at this
                      // instant , defaults included , travel with the
                      // engagement and are the FILE's from here on.
                      await openDossier({
                        vehicleId: simulation.vehicleId,
                        intention: "financement",
                        amountFinanced: simulation.amountFinanced,
                        depositPct: simulation.depositPct,
                        durationMonths: simulation.durationMonths,
                        // `OD-37` R7: where the deposit is, declared at this very step.
                        ...declaration,
                      });
                      // The file exists; the SCREEN has to be told. Read before
                      // the navigation, so « Suivi » is painted with his own
                      // file rather than with the emptiness it still held.
                      await relireLeDossier();
                      // `AVAILABILITY-QUEUE`, `M2`, point 4: the server just
                      // purged every OTHER front position this choice closed;
                      // re-read so a stale entry does not linger on the screen
                      // he might return to.
                      void loadFrontQueue()
                        .then(setFrontQueue)
                        .catch(() => undefined);
                      here.naviguer("/suivi");
                    } catch (failure) {
                      setIssueRefusal(
                        (failure as { message?: string } | null)?.message ??
                          t("common.error.connexionEchouee"),
                      );
                    } finally {
                      setIssueBusy(false);
                    }
                  })();
                }}
                /**
                 * `C3-A.2`: the drawing's arrow, back to the form he filled.
                 * Clearing the issue is what returns the screen to it , and
                 * without it a buyer who mistyped a salary had no way back
                 * except the browser, on a handset.
                 */
                onBack={() => {
                  setIssue(null);
                  setIssueRefusal(null);
                }}
                onSeeAffordable={() => {
                  here.naviguer("/recherche");
                }}
                onBuyCash={() => {
                  here.naviguer("/recherche");
                }}
              />
            );
          }

          // `S5-4`: the vehicle sheet's path with no car read (a reload, or a read that failed) is a wait with a way out, not a fall-through.
          if (ecran === "vehicule" && vehicleDetail === null) {
            return (
              <NoWayOut
                testid="vehicule-sans-fiche"
                message={t("client.session.chargement")}
                action={t("client.vehicule.retourRecherche")}
                onAction={() => {
                  here.naviguer("/recherche");
                }}
              />
            );
          }

          if (ecran === "vehicule" && vehicleDetail !== null && visiting) {
            return (
              <Visit
                title={vehicleDetail.title}
                // `CW.3b`: the NAME arrives through the port. The telephone
                // does not , no door answers a dealership's number , so the
                // `tel:` control still does not render, and `P1-CAT-010`'s
                // `source: "appel"` door stays built, tested and unreachable
                // from a screen until one does.
                sellerName={vehicleDetail.sellerName}
                city={vehicleDetail.city}
                district={vehicleDetail.district}
                distanceKm={vehicleDetail.distanceKm}
                travelTime={vehicleDetail.travelTime}
                phone={null}
                dossierEnCours={dossier !== null}
                intentOpen={intentOpen}
                intentRefusal={intentRefusal}
                onBack={() => {
                  setVisiting(false);
                }}
                onAskVisit={() => {
                  void askVisit(vehicleDetail.id, "app").catch(() => undefined);
                }}
                onCall={() => {
                  void askVisit(vehicleDetail.id, "appel").catch(() => undefined);
                }}
                onOpenIntent={() => {
                  setIntentOpen(true);
                }}
                onCheckEligibility={() => {
                  here.naviguer("/eligibilite");
                }}
                onFinance={() => {
                  here.naviguer("/eligibilite");
                }}
                onCash={() => {
                  // `T-2b`, `M2 (a)` (WF-CLI-07 « Je paie comptant », WF-CLI-09): the cash file born by
                  // the buyer's own door , no deposit, no term, `montant` the price he commits to
                  // (02 §2.3.1's own field, flagged) , then the fee screen, which already knows a
                  // cash file (« Ce paiement ne concerne que le véhicule »).
                  void (async () => {
                    try {
                      await openDossier({
                        vehicleId: vehicleDetail.id,
                        intention: "comptant",
                        amountFinanced: vehicleDetail.priceAmount,
                      });
                      await relireLeDossier();
                      // `AVAILABILITY-QUEUE`, `M2`, point 4: see the financing
                      // path's own comment above , the same purge, the same re-read.
                      void loadFrontQueue()
                        .then(setFrontQueue)
                        .catch(() => undefined);
                      here.naviguer("/dossier");
                    } catch (failure) {
                      setIntentRefusal(
                        (failure as { message?: string } | null)?.message ??
                          t("common.error.connexionEchouee"),
                      );
                    }
                  })();
                }}
              />
            );
          }

          if (ecran === "vehicule" && vehicleDetail !== null) {
            return (
              <VehicleSheet
                vehicule={vehicleDetail}
                estimate={quoteEstimate}
                coverage={coverage}
                verificationFee={verificationFee}
                buyerCity={session.identity.city === "" ? null : session.identity.city}
                onBack={() => {
                  here.naviguer("/recherche");
                }}
                onCheckEligibility={(stored) => {
                  // The simulation is CARRIED, not recomputed , `WF-CLI-04`.
                  setSimulation({
                    vehicleId: stored.vehicleId,
                    monthlyAmount: stored.monthlyAmount,
                    amountFinanced: stored.amountFinanced,
                    durationMonths: stored.durationMonths,
                    depositPct: stored.depositPct,
                    engaged: vehicleDetail.engaged,
                  });
                  setIssue(null);
                  setIssueRefusal(null);
                  here.naviguer("/eligibilite");
                }}
                onAskVisit={() => {
                  setVisiting(true);
                  setIntentOpen(false);
                }}
              />
            );
          }

          if (ecran === "queueFront") {
            return (
              <QueueFrontScreen
                entries={frontQueue}
                onBack={() => {
                  here.naviguer("/recherche");
                }}
                onChoose={(vehicleId) => {
                  // `WF-CLI-08`/`WF-CLI-09`'s own door , the car's fiche, where
                  // every engagement path already lives. No new mechanism: the
                  // buyer is routed to the SAME screen `onCheckEligibility`
                  // above reaches from, and chooses financing or cash there.
                  here.naviguer(`/vehicule/${vehicleId}`);
                }}
              />
            );
          }

          if (ecran === "recherche" || ecran === "accueil") {
            return (
              <>
                {/* `AVAILABILITY-QUEUE`, `M2`, point 3: the entry into « Véhicules
                    disponibles pour vous », shown only while at least one front
                    position stands , `R81`, never a control to an empty screen. */}
                {frontQueue.length > 0 ? (
                  <button
                    type="button"
                    data-testid="recherche-queue-front"
                    onClick={() => {
                      here.naviguer("/queue-front");
                    }}
                    className="mx-5 mt-4 w-[calc(100%-2.5rem)] rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-left text-sm font-medium text-indigo-700"
                  >
                    {t("client.queueFront.titre")} · {String(frontQueue.length)}
                  </button>
                ) : null}
                <Recherche
                  vehicles={park}
                  favourites={favourites}
                  passedList={passedList}
                  everywhere={everywhere}
                  cities={cities}
                  makes={makes}
                  city={city}
                  discoverOpen={discoverOpen}
                  radius={radius}
                  budget={budget}
                  make={make}
                  text={text}
                  onCity={setCity}
                  onRadius={setRadius}
                  onBudget={setBudget}
                  onMake={setMake}
                  tri={tri}
                  onTri={setTri}
                  onText={setText}
                  onKeep={(id: string) => {
                    // Optimistic, then the door. The heart must answer the finger
                    // at once on a Bamako handset; if the door refuses, the next
                    // read puts the list back.
                    const kept = !favourites.includes(id);
                    setFavourites(kept ? [...favourites, id] : favourites.filter((one) => one !== id));
                    void keepVehicle(id, kept).catch(() => undefined);
                  }}
                  onPass={(id: string) => {
                    setPassedList(passedList.includes(id) ? passedList : [...passedList, id]);
                    void passVehicle(id, true).catch(() => undefined);
                  }}
                  onClearPassed={() => {
                    setPassedList([]);
                    void clearPassed().catch(() => undefined);
                  }}
                  onOpen={(id: string) => {
                    here.naviguer(`/vehicule/${id}`);
                  }}
                />
              </>
            );
          }
          if (ecran === "paiement") {
            /**
             * `round C3-A`, `C3-A.2`: **`WF-CLI-10` §1 to §5.**
             *
             * The three answers are read here, where the file is already known,
             * and handed down whole. The screen composes nothing: `payments`
             * answers the amounts from its own versioned percentage,
             * `simulation` answers what financing the Hakili fee costs over the
             * term for a figure it is handed, and `dossier` serves the mandate's
             * clauses with their version.
             *
             * A buyer who reaches this path with no file sees the tracking
             * screen's own emptiness rather than a payment screen about nothing.
             */
            if (dossier === null) return <Suivi dossier={dossier} naviguer={here.naviguer} />;
            return (
              <PaymentScreen
                dossierId={dossier.id}
                vehicleLabel={dossier.vehicleLabel}
                sellerName={dossier.sellerName}
                ownerName={dossier.ownerName ?? null}
                dossierReference={dossier.reference}
                buyerPhone={session.identity.phone}
                // `L-1`, item 3: the buyer's terms are the FILE's, read by his own door.
                load={(id) =>
                  app.loadPaiement(id, {
                    depositPct: dossier.depositPct ?? null,
                    durationMonths: dossier.durationMonths ?? null,
                  })
                }
                accept={app.acceptMandate}
                ask={app.askVerificationFee}
                poll={app.readPayment}
                cancelDeclaration={app.cancelFeeDeclaration}
                declarePaid={app.declareFeePaid}
                launch={app.launchVerification}
                onBack={() => {
                  here.naviguer("/suivi");
                }}
                onLaunched={() => {
                  // The file has left `intake`. His tracking card is where the
                  // next stage is read, and it is re-read on arrival there.
                  here.naviguer("/suivi");
                }}
              />
            );
          }
          if (ecran === "offres") {
            // `L-2`, `M2`: WF-CLI-22 , the comparison, the confirmation with
            // the code, the locked comparison after the choice. Rendered here,
            // where the door has been asked, like `suivi` and `paiement`.
            if (offers === null) {
              return <p className="p-6 text-sm text-gray-500">{t("client.session.chargement")}</p>;
            }
            return (
              <>
                {offersWord === null ? null : (
                  <p role="alert" className="px-5 pt-4 text-sm text-rose-600" data-testid="offres-refus">
                    {offersWord}
                  </p>
                )}
                <Offres
                  offers={offers}
                  // F10: the chargé's agreement is read from a label, and no door
                  // serves the file's chargé's; the masculine key stands, flagged.
                  caseworkerAgreement={null}
                  askCode={app.askAcceptanceCode}
                  accept={app.acceptOffer}
                  onAccepted={() => {
                    // The choice is made: the file is re-read (the card's next
                    // state is the door's), and Suivi says what comes next.
                    setOffers(null);
                    void relireLeDossier().then(() => {
                      here.naviguer("/suivi");
                    });
                  }}
                  naviguer={here.naviguer}
                />
              </>
            );
          }
          if (ecran === "comptant") {
            // `T-2b` (WF-CLI-28): the cash purchase's conclusion , two beneficiaries, the declaration, the bon.
            return (
              <CashScreen
                view={cash}
                declare={() => (dossierId === null ? Promise.resolve() : declareCash(dossierId))}
                naviguer={here.naviguer}
              />
            );
          }
          if (ecran === "retrait") {
            // `T-2` (WF-CLI-27): the bon de retrait, or the block and its reason, by the buyer's own door.
            return <PickupVoucherScreen view={voucher} naviguer={here.naviguer} />;
          }
          if (ecran === "suivi") {
            // `round CW`, `CW.5`: « Où en est ma demande ». It is rendered
            // here, where `/me/dossiers` has already been read, rather than
            // through the stub table. `C3-H`: with the launch door and the
            // re-read, so the card's own control calls its door.
            return (
              <Suivi
                dossier={dossier}
                naviguer={here.naviguer}
                coverage={dossier?.controlCoverage ?? null}
                lancer={app.launchVerification}
                abandonner={app.abandon}
                acceptProposedPrice={app.acceptPrice}
                lireRapport={app.readReport}
                trancherGarantie={app.settleWarranty}
                fileFee={fileFee}
                declareFileFee={declareFileFee}
                voucher={voucher ?? null}
                acheterComptant={acheterComptant}
                comptantOuvert={cash !== null && cash !== undefined}
                comptantRemisLe={cash?.voucher?.presentedAt ?? null}
                relire={async () => {
                  await relireLeDossier();
                }}
                liberer={() => {
                  // `L-3b`, `M3` (WF-CLI-21 step 4): the refused file released from the app's seat,
                  // the search shown; it stays consultable on the server (P1-DOS-008).
                  setDossier(null);
                  here.naviguer("/recherche");
                }}
              />
            );
          }
          if (ecran === "declaration") {
            if (profileView === null) {
              return profileRefusal === undefined ? (
                <p className="text-sm text-gray-500">{t("client.declaration.enregistrement")}</p>
              ) : (
                <p role="alert" className="text-sm text-rose-700">
                  {profileRefusal}
                </p>
              );
            }
            return (
              <ProfilDeclare
                view={profileView}
                refusal={profileRefusal}
                retour={() => {
                  here.naviguer("/profil");
                }}
                enregistrer={async (write) => {
                  try {
                    setProfileNotices(await app.saveProfile(write));
                    setProfileRefusal(undefined);
                    setProfileView(null);
                    here.naviguer("/profil");
                  } catch (failure) {
                    setProfileRefusal(messageOf(failure, t("common.error.reseau")));
                  }
                }}
              />
            );
          }
          if (ecran === "profil") {
            return (
              <Profil
                identity={session.identity}
                notices={profileNotices}
                onNoticesShown={() => {
                  setProfileNotices([]);
                }}
                dossier={
                  dossier === null
                    ? null
                    : // The drawing's row follows the buyer's own intent
                      // (« Mon achat comptant » / « Mon dossier de
                      // financement »), and `GET /me/dossiers/{id}` does not
                      // answer `intentionAchat`. So the neutral label stands
                      // and the divergence is flagged rather than invented.
                      { label: dossier.vehicleLabel, intention: null }
                }
                naviguer={here.naviguer}
                deconnecter={deconnecter}
                handsets={handsets}
                retire={async (deviceId) => {
                  await app.retireDevice(deviceId);
                  await reloadHandsets();
                }}
                markRead={async () => {
                  await app.readNotices();
                  await reloadHandsets();
                }}
                /** `round N+3`, `S2-14`: the client's own latest declaration, read once per session. */
                declaration={latestDeclaration}
                onUpdateDeclaration={() => {
                  setProfileView(null);
                  here.naviguer("/profil/declaration");
                }}
              />
            );
          }
          const Ecran = ECRANS[ecran];
          return (
            <Ecran
              params={params}
              dossier={
                <Dossier
                  dossier={dossier}
                  documents={documents}
                  fees={fees}
                  deposit={depositDocument}
                  submit={submitDocuments}
                  deconnecter={deconnecter}
                  // `FX-1`, `M2`: the start beside « Aucun dossier en cours », per the
                  // founder's rule of 24 September. The screen is handed the gesture.
                  recommencer={() => {
                    here.naviguer("/recherche");
                  }}
                />
              }
            />
          );
        }}
      </ClientShell>
    </DemandeFonctionnaliteProvider>
  );
}
